import React, {
  useCallback, useState, useMemo, useEffect,
} from 'react';
import { Col, Row, Select } from 'antd';
import { GoldOutlined } from '@ant-design/icons';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';

import { getType } from './workflowHelpers';
import HoverHelp from '../../common/HoverHelp';

const HOVER_TEXT = `
Use this step to update material quantities from your material database. This is set to debit by default.
`;

const DISABLED_TEXT = 'This step requires at least one Materials table field';

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the <b>Select Material Table Field(s)</b> and <b>Select Quantity Type</b> field to be configured.
    </div>
  ),
};

const quantityOptions = [
  { value: 'quantity', label: 'Quantity On-Hand' },
  { value: 'quantityAllocated', label: 'Quantity Allocated' },
];

const transactionOptions = [
  { value: 'Subtract', label: 'Subtract' },
  { value: 'Add', label: 'Add' },
  { value: 'Transfer', label: 'Transfer' },
];

export default function WorkflowMaterialDebitStep({
  setElements,
  setDataMap,
  isDisplay,
  sections = [],
  setDisableNext,
} = {}) {
  return function _({
    id,
    draggable,
    disabled,
    data = {},
  }) {
    const {
      fields: initialSelected = [],
      quantityType: initialQuantityType = 'quantity',
      transaction: initialTransaction,
    } = data;
    const [selected, setSelected] = useState(initialSelected);
    const [quantityType, setQuantityType] = useState(initialQuantityType);
    const [transaction, setTransaction] = useState(initialTransaction);

    const disableNext = useMemo(() => (
      !selected.length || !quantityType
    ), [selected, quantityType]);

    useEffect(() => {
      if (!setDisableNext) return;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [setDisableNext, id, disableNext]);

    const setters = {
      fields: setSelected,
      quantityType: setQuantityType,
      transaction: setTransaction,
    };
    const onChange = useCallback((key) => (newValue) => {
      setDataMap((dataMap) => {
        const {
          [id]: oldData = {},
        } = dataMap;
        return {
          ...dataMap,
          [id]: {
            ...oldData,
            [key]: newValue,
          },
        };
      });
      setters[key](newValue);
    }, [id, setDataMap]);

    const materialTableFields = useMemo(() => (
      sections.reduce((sAcc, section) => {
        const { fields = [] } = section;
        return sAcc.concat(
          fields.reduce((fAcc, field) => {
            const { id: fieldId, configProps: { title } = {} } = field;
            const type = getType(field);
            if (type === 'table - Materials') fAcc.push({ label: title, value: fieldId });
            return fAcc;
          }, []),
        );
      }, [])
    ), [sections]);

    const title = `Update Materials${disabled ? ' - DISABLED' : ''}`;

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        Icon={GoldOutlined}
        type="materialDebit"
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={disableNext ? ERROR_DETAILS : undefined}
      >
        {!draggable && (
        <div>
          <Row style={{ marginTop: 5 }}>
            Select Material Table Field(s):
          </Row>
          <Row style={{ margin: '10px 0px' }}>
            <Select
              mode="multiple"
              onChange={onChange('fields')}
              value={selected}
              options={materialTableFields}
              optionFilterProp="label"
            />
          </Row>
          <Row style={{ marginTop: 5 }}>
            Select Quantity Type:
          </Row>
          <Row style={{ margin: '10px 0px' }}>
            <Select
              onChange={onChange('quantityType')}
              value={quantityType}
              options={quantityOptions}
              optionFilterProp="label"
            />
          </Row>
          <Row style={{ marginTop: 5 }}>
            <Col style={{ padding: '0px 5px' }}>Transaction Type:</Col>
            <Col>
              <HoverHelp
                placement="topRight"
                content={(
                  <div style={{ width: 300 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 5 }}>Subtract/Add:</div>
                    <div>
                      Quantity value is added or subtracted from the configured quantity type.
                      This will override the Debit/Credit column if set.
                    </div>
                    <div style={{ fontWeight: 'bold', marginBottom: 5, marginTop: 10 }}>Transfer:</div>
                    <div>
                      Transfers the quantity value from the location in the &apos;location&apos;
                      column to the location in the &apos;To Location&apos; column.
                    </div>
                  </div>
                )}
              />
            </Col>
          </Row>
          <Row style={{ margin: '10px 0px' }}>
            <Select
              onChange={onChange('transaction')}
              value={transaction}
              options={transactionOptions}
              optionFilterProp="label"
              allowClear
            />
          </Row>
          <WorkflowHandle type="target" position="top" disabled={isDisplay} />
        </div>
        )}
      </WorkflowActionNode>
    );
  };
}
